'use client'

import { useTranslations } from 'next-intl'
import type { OpenStatus } from '@/lib/hours/openStatus'

/** "22:00" -> "10:00 PM" (formato amigable para el cliente). Tolera ausencia:
 *  un horario parcial (`{closed:false}` sin horas) no debe reventar el render. */
export function formatTime12h(hhmm: string | null | undefined): string {
  if (!hhmm) return ''
  const [hStr, mStr] = hhmm.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm
  const period = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${period}`
}

/**
 * Texto + tono del estado de apertura, listo para pintar. Es un hook (usa su
 * propio `useTranslations`) para que el badge y los heroes lo compartan sin
 * pasarse `t`. null => estado desconocido (no renderizar).
 *
 *  - Abierto, faltan >60 min para cerrar  → "Abierto".
 *  - Abierto, faltan ≤60 min para cerrar  → "Cierra en {n} min" (conteo regresivo).
 *  - Cerrado, faltan ≤60 min para abrir   → "Abre en {n} min" (conteo regresivo).
 *  - Cerrado, falta más                    → "Abre hoy a las {hora}" / "Abre el {día} a las {hora}".
 */
export function useOpenStatusLabel(
  status: OpenStatus | null,
): { text: string; tone: 'open' | 'soon' | 'closed' } | null {
  const t = useTranslations('public.microsite')
  if (!status) return null

  if (status.isOpen) {
    if (status.minutesUntilClose != null && status.minutesUntilClose <= 60) {
      return { text: t('openStatus.closesIn', { minutes: status.minutesUntilClose }), tone: 'soon' }
    }
    return { text: t('openStatus.open'), tone: 'open' }
  }

  if (status.minutesUntilOpen != null && status.minutesUntilOpen <= 60) {
    return { text: t('openStatus.opensIn', { minutes: status.minutesUntilOpen }), tone: 'soon' }
  }
  if (status.nextOpen) {
    const time = formatTime12h(status.nextOpen.time)
    const text = status.opensToday
      ? t('openStatus.opensTodayTime', { time })
      : t('openStatus.opensDayTime', { day: t(`days.${status.nextOpen.day}`), time })
    return { text, tone: 'closed' }
  }
  return { text: t('openStatus.closed'), tone: 'closed' }
}

/** Pill de estado con punto. null => no renderiza (estado desconocido). */
export function OpenStatusBadge({ status }: { status: OpenStatus | null }) {
  const label = useOpenStatusLabel(status)
  if (!label) return null

  const dot =
    label.tone === 'open'
      ? { background: '#2ecc71', boxShadow: '0 0 0 3px rgba(46,204,113,0.25)' }
      : label.tone === 'soon'
        ? { background: '#f59e0b', boxShadow: '0 0 0 3px rgba(245,158,11,0.22)' }
        : { background: '#9ca3af' }

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white px-3 py-1 text-sm font-medium"
      role="status"
    >
      <span className="w-[7px] h-[7px] rounded-full" style={dot} />
      {label.text}
    </span>
  )
}

/**
 * Texto explicativo cuando el local NO acepta pedidos en línea. Dos casos:
 *  - cerrado: "Abre {día} a las {hora}".
 *  - abierto pero dentro del buffer de cierre (last call): "Pedidos en línea
 *    cerrados · cierra a las {hora}".
 * Devuelve null cuando sí se aceptan pedidos o el estado es desconocido.
 */
export function ClosedNotice({ status }: { status: OpenStatus | null }) {
  const t = useTranslations('public.microsite')
  if (!status || status.isAcceptingOrders) return null

  let detail: string
  if (status.isOpen && status.closesAt) {
    // Abierto pero ya pasó el último pedido (buffer antes del cierre).
    detail = t('openStatus.lastCall', { time: formatTime12h(status.closesAt) })
  } else if (status.nextOpen) {
    detail = t('openStatus.opensDayTime', {
      day: t(`days.${status.nextOpen.day}`),
      time: formatTime12h(status.nextOpen.time),
    })
  } else {
    detail = t('openStatus.closedNow')
  }

  return (
    <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-center">
      <p className="text-sm font-semibold text-amber-900">{t('openStatus.notAccepting')}</p>
      <p className="text-xs text-amber-800 mt-0.5">{detail}</p>
    </div>
  )
}
